/**
 * Market reference data and the canonical instrument key.
 *
 * Lives in `shared` because the browser needs lot sizes and strike steps to
 * render a chain, and the API needs them to validate an order. Two copies of
 * a lot size is a money bug waiting to happen.
 */
export const EXCHANGES = ['NSE', 'BSE', 'MCX'];
export const SEGMENTS = ['NSE_FO', 'BSE_FO', 'NSE_CM', 'BSE_CM', 'MCX_FO'];
export const INSTRUMENT_TYPES = ['OPTION', 'FUTURE', 'EQUITY', 'INDEX'];
export const SIMULATION_SESSION_STATUSES = [
    'STARTING',
    'RUNNING',
    'STOPPING',
    'STOPPED',
    'FAILED',
];
export const INDEX_SPECS = {
    NIFTY: { key: 'NIFTY', name: 'NIFTY 50', exchange: 'NSE', segment: 'NSE_FO', step: 50, lot: 65, freezeQty: 3510 },
    BANKNIFTY: { key: 'BANKNIFTY', name: 'NIFTY BANK', exchange: 'NSE', segment: 'NSE_FO', step: 100, lot: 30, freezeQty: 1440 },
    FINNIFTY: { key: 'FINNIFTY', name: 'NIFTY FIN SERVICE', exchange: 'NSE', segment: 'NSE_FO', step: 50, lot: 60, freezeQty: 3240 },
    SENSEX: { key: 'SENSEX', name: 'SENSEX', exchange: 'BSE', segment: 'BSE_FO', step: 100, lot: 20, freezeQty: 1000 },
    BANKEX: { key: 'BANKEX', name: 'BANKEX', exchange: 'BSE', segment: 'BSE_FO', step: 100, lot: 30, freezeQty: 900 },
};
export const INDEX_KEYS = Object.keys(INDEX_SPECS);
/**
 * Which indices are always-on Tier 1 in the feed pool (ARCHITECTURE.md §4.4).
 *
 * Three, not five: 3 × (weekly ±16, next-weekly ±8, monthly ±5) × CE/PE = 366
 * instruments against a 560 local ceiling. Five would be 610 and overrun.
 * Adding a client-code shard (§4.2) is what buys the other two.
 */
export const TIER1_INDICES = ['NIFTY', 'BANKNIFTY', 'SENSEX'];
export const resolveAtm = (spot, step) => Math.round(spot / step) * step;
/** Strike ladder around ATM, inclusive of both wings. */
export function strikeLadder(spot, step, half) {
    const atm = resolveAtm(spot, step);
    const out = [];
    for (let i = -half; i <= half; i += 1)
        out.push(atm + i * step);
    return out;
}
export function canonicalKeyString(s) {
    return [
        s.exchange,
        s.segment,
        s.instrumentType,
        s.underlying.toUpperCase(),
        s.expiry ?? '',
        s.strike == null ? '' : String(s.strike),
        s.optionType ?? '',
    ].join('|');
}
/** Lots → units. The only place this multiplication should exist. */
export const lotsToQty = (lots, lotSize) => lots * lotSize;
/**
 * Does this quantity need slicing to clear the exchange freeze limit?
 * Returns the child order sizes, or a single element if no slice is needed.
 */
export function sliceForFreeze(qty, freezeQty, lotSize) {
    if (qty <= freezeQty)
        return [qty];
    const maxLotsPerSlice = Math.floor(freezeQty / lotSize);
    const chunk = maxLotsPerSlice * lotSize;
    if (chunk <= 0)
        return [qty]; // one lot already exceeds freeze — RMS gate refuses
    const out = [];
    let left = qty;
    while (left > 0) {
        const take = Math.min(chunk, left);
        out.push(take);
        left -= take;
    }
    return out;
}
/**
 * How a contract is written for a person — "NIFTY 22950 PE".
 *
 * ONE SPELLING, EVERYWHERE, because the order book shows the same order
 * twice: once from the engine's live event and again from the server after
 * a reload. The engine used to send the catalogue's raw trading symbol
 * (`NIFTY2601922950PE`) and the server built this one, so a row changed
 * from unreadable to readable on refresh and looked like two different
 * instruments.
 *
 * The expiry is deliberately absent. Every leg on one screen is almost
 * always the same expiry, and the date doubles the width of the one column
 * a trader scans down.
 */
export function contractLabel(input) {
    const { underlying, strike, optionType } = input;
    if (optionType && strike !== null && strike !== undefined && strike > 0) {
        const on = expiryCode(input.expiry);
        // No grouping separators: a strike is an identifier here, not a price,
        // and "22,950 PE" invites reading the comma as a decimal abroad.
        return `${underlying}${on ? ` ${on}` : ''} ${Math.round(strike)} ${optionType}`;
    }
    return input.fallback?.trim() || underlying;
}
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
/**
 * `19OCT` — the shortest spelling of an expiry a desk reads without pausing.
 *
 * No year: an option chain never lists the same day and month twice, and
 * the four characters it saves are four characters of strike.
 */
export function expiryCode(expiry) {
    if (expiry === null || expiry === undefined)
        return null;
    const date = typeof expiry === 'string' ? new Date(`${expiry.slice(0, 10)}T00:00:00Z`) : expiry;
    const time = date.getTime();
    if (!Number.isFinite(time))
        return null;
    return `${String(date.getUTCDate()).padStart(2, '0')}${MONTHS[date.getUTCMonth()]}`;
}
//# sourceMappingURL=market.js.map